/**
 * Formatter date/datetime in italiano.
 * Estratti dai duplicati locali in UploadsPage / CatalogsPage / OrdersPage / AdminPanel.
 *
 * Pattern:
 *   formatDate('2026-05-12T08:30:00Z')  → '12/05/2026, 10:30'
 *   formatDateTime('2026-05-12T08:30:00Z') → '12/05/26, 10:30'
 *
 * Differenza: formatDate usa year:'numeric' (4 cifre), formatDateTime year:'2-digit' (2 cifre).
 * Mantieni entrambi i comportamenti pre-esistenti.
 */

export function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Normalizza una data/ora che arriva dal backend SENZA offset
 * ("2026-09-20T08:00:00") in modo che venga letta come UTC.
 *
 * Perché serve: dove il client Mongo non è tz_aware, pymongo restituisce
 * datetime naive e FastAPI li serializza senza offset. `new Date()` su una
 * stringa senza offset la interpreta come ora LOCALE — in Italia CEST — e
 * mostra due ore MENO di quando la cosa è successa davvero.
 *
 * Lo stesso campo può arrivare in entrambe le forme nella stessa sessione
 * (es. POST /nota risponde con un datetime aware, una rilettura restituisce
 * lo stesso valore naive): una stringa che ha già 'Z' o un offset resta
 * intatta, così la conversione non viene mai applicata due volte.
 *
 * Usage:
 *   isoUtc('2026-09-20T08:00:00')       → '2026-09-20T08:00:00Z'
 *   isoUtc('2026-09-20T08:00:00Z')      → '2026-09-20T08:00:00Z'  (invariata)
 *   isoUtc('2026-09-20T10:00:00+02:00') → invariata
 *   isoUtc(null)                        → null   (il chiamante decide)
 */
export function isoUtc(value) {
  if (!value) return value;
  const s = String(value);
  return /(?:Z|z|[+-]\d{2}:?\d{2})$/.test(s) ? s : `${s}Z`;
}

/**
 * Minuti trascorsi da una data/ora del backend (naive UTC, vedi isoUtc sopra)
 * a adesso. Usato per le soglie di eta' dello stato del ponte WhatsApp
 * (StatoWhatsApp.js, HomePage.js): staccato da quanto, codice da quanto.
 *
 * Usage:
 *   minutiDa('2026-09-25T08:00:00')  → minuti da allora a ora
 *   minutiDa(null)                   → null
 */
export function minutiDa(value) {
  if (!value) return null;
  const t = new Date(isoUtc(value)).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
}

/**
 * Formatta un prezzo in formato italiano: "€ 12,50".
 * - Virgola come separatore decimale (non punto, mai)
 * - Sempre 2 decimali
 * - "€ 0,00" se il valore è null/undefined/NaN
 *
 * Usage:
 *   formatPrice(12.5)   → "€ 12,50"
 *   formatPrice(null)   → "€ 0,00"
 *   formatPrice(-3.14)  → "€ -3,14"
 */
/**
 * Parsing numerico tollerante all'input italiano: virgola decimale +
 * punto migliaia ("1.234,56" → 1234.56). Equivalente JS di
 * backend/money_utils.parse_eu_amount — usarlo per OGNI input utente di
 * importi/quantità in formato IT (mai un parseFloat/Number nudo con solo
 * .replace(',', '.'): un punto migliaia lo tronca silenziosamente).
 *
 * Usage:
 *   parseNum("1.234,56") → 1234.56
 *   parseNum("12,5")     → 12.5
 *   parseNum(null)       → NaN
 */
export function parseNum(v) {
  if (v === null || v === undefined) return NaN;
  // Già numerico (valori impostati da codice, es. preset rate): passa com'è.
  // Il parsing IT sotto tratterebbe il punto decimale di String(v) come
  // separatore migliaia (1127.28 → 112728).
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(/\./g, '').replace(',', '.');
  return parseFloat(s);
}

/**
 * Parsing di un campo prezzo mentre l'utente lo DIGITA. Differisce da
 * parseNum: lì il punto è sempre separatore migliaia, qui no — chi scrive un
 * prezzo su tastiera usa spesso il punto come decimale ("12.5"), e trattarlo
 * come migliaia lo trasformerebbe in 125.
 *
 * Regola (la stessa del campo prezzo della cassa, VenditeApp):
 * - c'è una virgola  → è lei il separatore decimale, i punti sono migliaia
 *   ("1.234,56" → 1234.56)
 * - non c'è virgola  → la stringa è già in formato JS ("12.5" → 12.5)
 *
 * Ritorna un numero, oppure '' se il campo è vuoto o non interpretabile —
 * MAI NaN verso lo state di un input controllato. Nota: 0 è un prezzo
 * legittimo, quindi non si può usare `parseFloat(x) || ''` (0 è falsy e
 * verrebbe cancellato: era il bug D1-F2 dell'audit ondata 4).
 *
 * Usage:
 *   parsePriceInput("89,90")    → 89.9
 *   parsePriceInput("1.234,56") → 1234.56
 *   parsePriceInput("12.5")     → 12.5
 *   parsePriceInput("0")        → 0
 *   parsePriceInput("")         → ''
 */
export function parsePriceInput(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? v : '';
  const s = String(v).trim();
  if (s === '') return '';
  const normalizzato = s.includes(',')
    ? s.replace(/\./g, '').replace(',', '.')
    : s;
  const n = parseFloat(normalizzato);
  return Number.isNaN(n) ? '' : n;
}

/**
 * Valore da mostrare in un campo prezzo: numero → stringa con virgola
 * decimale, come l'avrebbe scritto l'utente ("89.9" → "89,9").
 */
export function formatPriceInput(v) {
  if (v === null || v === undefined || v === '') return '';
  return String(v).replace('.', ',');
}

export function formatPrice(value) {
  // Usa NON-BREAKING SPACE ( ) tra '€' e cifra: garantisce che il
  // browser non vada mai a capo lasciando '€' su una riga e l'importo
  // sull'altra. Mandatory regola d'app.
  if (value === null || value === undefined || isNaN(value)) return '€ 0,00';
  return '€ ' + Number(value).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
